/**
 * Create flow page using FlowForm, mirroring CreateScriptPage behavior
 */
import React from "react";
import { useNavigate, Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import FlowForm from "../../components/flows/FlowForm";
import { useCreateScript } from "../../hooks/useScripts";
import { ScriptCreate, ScriptUpdate } from "../../types/script";
import { useCampaigns } from "../../hooks/useCampaigns";
import { useAuth } from "../../hooks/useAuth";

const CreateFlowPage: React.FC = () => {
  const navigate = useNavigate();
  const createScript = useCreateScript();
  const { data: campaignsData } = useCampaigns({ page_size: 200 });
  const { user } = useAuth();
  const isAdmin = (user?.permissions || []).includes("admin");
  const hasCreate =
    isAdmin || (user?.permissions || []).includes("flows.create");
  const campaignOptions = (campaignsData?.campaigns || []).map((c: any) => ({
    id: c.id,
    name: c.name,
  }));

  const handleSubmit = async (data: ScriptCreate | ScriptUpdate) => {
    try {
      const newScript = await createScript.mutateAsync(data as ScriptCreate);
      navigate(`/flows/${newScript.id}`);
    } catch (error) {
      console.error("Failed to create flow:", error);
    }
  };

  const handleCancel = () => {
    navigate("/flows");
  };

  if (!hasCreate) {
    return (
      <div className="p-4">
        <div className="text-red-600">
          You do not have permission to create flows.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/flows" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Flow</h1>
          <p className="text-sm text-gray-500">
            Create a new conversation flow for your campaign
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl">
        <FlowForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={createScript.isPending}
          submitText="Create Flow"
          campaignOptions={campaignOptions}
          hideReplayNodeGroups
        />
      </div>
    </div>
  );
};

export default CreateFlowPage;
